"use client";

/**
 * usePortfolio Hook
 *
 * Fetches portfolio summary data for the authenticated user.
 * Uses session cookie for auth; wallet address is optional fallback.
 */

import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import type { PortfolioSummary } from "@/types/api.types";

// Stale time: 10 seconds
const STALE_TIME = 10 * 1000;

export interface UsePortfolioReturn {
  /** Portfolio summary data */
  portfolio: PortfolioSummary | null;
  /** Loading state */
  isLoading: boolean;
  /** Whether data is being refetched */
  isFetching: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
}

export function usePortfolio(): UsePortfolioReturn {
  const { isAuthenticated, session } = useAuth();
  const userId = session?.user?.id;

  const query = useQuery({
    queryKey: ["portfolio", userId],
    queryFn: async () => {
      try {
        // Session cookie handles auth — no wallet address needed
        const data = await api.allocations.getPortfolio();
        return data;
      } catch (err) {
        // If portfolio endpoint returns 404 (no allocations), return default values
        if (err instanceof Error && err.message.includes("404")) {
          return {
            totalAllocated: 0,
            totalCurrentValue: 0,
            totalUnrealizedPnl: 0,
            totalUnrealizedPnlPercent: 0,
            totalRealizedPnl: 0,
            allocationCount: 0,
            availableBalance: 500000, // Default paper trading balance
          };
        }
        throw err;
      }
    },
    staleTime: STALE_TIME,
    enabled: isAuthenticated,
  });

  return {
    portfolio: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
